import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    IconButton,
    Tabs,
    Tab,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Alert,
    Divider,
    InputAdornment
} from '@mui/material';
import {
    ManageAccounts as ManageIcon,
    Edit as EditIcon,
    PersonAdd as PersonAddIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    AccessTime as ClockIcon,
    Warning as WarningIcon,
    Event as EventIcon,
    CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import moment from 'moment';
import { getUser } from '../../../lib/connector/userQueries';
import { putShift, getShifts } from '../../../lib/connector/shiftQueries';
import { useToast } from '../ToastProvider/ToastProvider';
import { deleteAssignment, putAssignment } from '../../../lib/connector/assignmentsQueries';
import { checkAccessLevel } from '../../../lib/authenticate';
import EmployeeSelectionList from './EmployeeSelectionList';

interface Employee {
    name: string;
    availability: Record<string, any>;
    positionAssignments: any[];
    id: string;
    employeeShifts: Record<string, any>[];
}

interface ManageShiftModalProps {
    open: boolean;
    onClose: () => void;
    shift: any;
    selectedDay?: moment.Moment;
    availabilities: any[];
    defaultAvailabilities: any[];
    selectedSchedule: any;
    positions: any[];
    shifts: any[];
    setShifts: React.Dispatch<React.SetStateAction<any[]>>;
    assignments: any[];
    setAssignments: React.Dispatch<React.SetStateAction<any[]>>;
    userData: any;
    accessLevel: string | null;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`shift-tabpanel-${index}`}
            aria-labelledby={`shift-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function ManageShiftModal({
    open,
    onClose,
    shift,
    selectedDay,
    selectedSchedule,
    availabilities,
    defaultAvailabilities,
    positions,
    shifts,
    setShifts,
    assignments,
    setAssignments,
    userData,
    accessLevel
}: ManageShiftModalProps) {
    const toast = useToast();
    const [tabValue, setTabValue] = useState(1);
    const [loading, setLoading] = useState(false);

    // Edit Shift State
    const [editFormData, setEditFormData] = useState({
        shiftName: '',
        daysOfWeek: [] as string[],
        startTime: '',
        endTime: '',
        position: '',
        staffNeeded: 1,
        isRecurring: true,
        shiftDate: moment().format('YYYY-MM-DD'),
        notes: '',
        assignedStaff: [] as string[]
    });
    const [originalShiftName, setOriginalShiftName] = useState('');

    // Assign Staff State
    const [assignmentType, setAssignmentType] = useState<'day' | 'shift'>('day');
    const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
    const [standardEmployees, setStandardEmployees] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [hideAssigned, setHideAssigned] = useState(false);
    const [employees, setEmployees] = useState<Employee[]>([]);

    const daysOfWeek = [
        { value: 'monday', label: 'Mon', fullLabel: 'Monday' },
        { value: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
        { value: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
        { value: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
        { value: 'friday', label: 'Fri', fullLabel: 'Friday' },
        { value: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
        { value: 'sunday', label: 'Sun', fullLabel: 'Sunday' }
    ];

    // Load data when modal opens
    useEffect(() => {
        if (open && shift) {
            // Load shift data for editing
            setEditFormData({
                shiftName: shift.shiftName || '',
                daysOfWeek: shift.daysOfWeek || [],
                startTime: shift.startTime || '',
                endTime: shift.endTime || '',
                position: shift.position || '',
                staffNeeded: shift.staffNeeded || 1,
                isRecurring: shift.isRecurring !== undefined ? shift.isRecurring : true,
                shiftDate: shift.shiftDate || moment().format('YYYY-MM-DD'),
                notes: shift.notes || '',
                assignedStaff: shift.assignedStaff || []
            });
            setOriginalShiftName(shift.shiftName || '');
            let assignedStaff = shift.assignedStaff
            setStandardEmployees(assignedStaff || []);
            assignments.map((assignment => {
                if (assignment.date === selectedDay?.format("YYYY-MM-DD") && assignment.shiftId === shift.shiftId) {
                    assignedStaff = assignment.assignees
                }
            }))
            setSelectedEmployees(assignedStaff || []);

            // Load employees for assignment
            loadEmployees();
        }
        setTabValue(checkAccessLevel(accessLevel) ? 0 : 1)
    }, [open, shift, assignmentType]);

    useEffect(() => {
        if (shift) {
            setAssignmentType(shift.isRecurring ? 'shift' : 'day');
        }
    }, [shift]);

    const loadEmployees = async () => {
        setLoading(true);
        try {
            let employeeList: Employee[] = [];
            await Promise.all([selectedSchedule.userId, ...(selectedSchedule.employees ?? [])].map(async (employee: string) => {
                await getUser(employee).then(async (response) => {
                    let positionAssignments: any[] = [];
                    let foundAvailability: any = {};
                    let foundDefaultAvailability: any = {};
                    let foundCustomAvailability = false;
                    await Promise.all(positions.map((position) => {
                        if (position.employees.includes(employee)) {
                            positionAssignments.push(position);
                        }
                    }));
                    await Promise.all(availabilities.map((availability) => {
                        if (availability.userId === employee && selectedDay?.format("YYYY-MM-DD") === availability.dateUserId.split("#")[0]) {
                            foundCustomAvailability = true;
                            foundAvailability = JSON.parse(JSON.stringify(availability))
                        }
                    }));
                    if (!foundCustomAvailability) {
                        await Promise.all(defaultAvailabilities.map((defaultAvailability) => {
                            if (defaultAvailability.userId === employee) {
                                foundDefaultAvailability = JSON.parse(JSON.stringify(defaultAvailability));
                            }
                        }));
                    }
                    const newEmployee: Employee = {
                        name: response[0].name,
                        positionAssignments: positionAssignments,
                        availability: foundCustomAvailability ? foundAvailability.availability : foundDefaultAvailability.availability,
                        id: employee,
                        employeeShifts: shifts.filter((s) => s.assignedStaff.includes(employee) &&
                            ((s.isRecurring && s.daysOfWeek.includes(selectedDay?.format('dddd').toLowerCase() || '')) ||
                            (!s.isRecurring && !s.cancelShiftId && s.shiftDate === selectedDay?.format('YYYY-MM-DD'))))
                    };
                    employeeList.push(newEmployee);
                });
            }));
            setEmployees(employeeList);
        } catch (error) {
            toast.showError('Error loading employees');
        } finally {
            setLoading(false);
        }
    };

    // Edit Shift Handlers
    const handleEditChange = (e: { target: { name: any; value: any; }; }) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.checked,
            daysOfWeek: e.target.checked ? editFormData.daysOfWeek : []
        });
    };

    const handleDaysChange = (event: any, newDays: any) => {
        setEditFormData({
            ...editFormData,
            daysOfWeek: newDays
        });
    };

    // Assign Staff Handlers
    const handleToggleEmployee = (employeeId: string) => {
        const newSelection = selectedEmployees.includes(employeeId)
            ? selectedEmployees.filter(id => id !== employeeId)
            : [...selectedEmployees, employeeId];

        setSelectedEmployees(newSelection);
        setStandardEmployees(newSelection);

        // Update edit form data as well
        setEditFormData(prev => ({
            ...prev,
            assignedStaff: newSelection
        }));
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Validation for edit tab
            if (tabValue === 0) {
                // Check if shift name is taken (if changed)
                if (editFormData.shiftName !== originalShiftName) {
                    const shiftNameTaken = shifts.some(s => s.shiftName === editFormData.shiftName);
                    if (shiftNameTaken) {
                        toast.showError("Shift name already taken. Please choose a different name.");
                        setLoading(false);
                        return;
                    }
                }

                // Validation: Check time logic
                if (editFormData.startTime && editFormData.endTime) {
                    const start = new Date(`2000-01-01T${editFormData.startTime}`);
                    const end = new Date(`2000-01-01T${editFormData.endTime}`);
                    if (start >= end && end.getHours() !== 0) {
                        toast.showWarning("End time should be after start time for same-day shifts.");
                    }
                }
            }

            if (assignmentType === 'day') {
                let customAssignment = true
                let foundShift: any = null
                shifts.map((s) => {
                    if (s.shiftId === shift.shiftId) {
                        foundShift = s
                        if (JSON.stringify(s.assignedStaff.sort()) === JSON.stringify(selectedEmployees.sort())) {
                            customAssignment = false
                        }
                    }
                })
                if (foundShift && !customAssignment) {
                    await deleteAssignment(foundShift!.scheduleId, foundShift!.shiftId, selectedDay!.format("YYYY-MM-DD"))
                    setAssignments(assignments.filter((a) => !(a.shiftId === foundShift!.shiftId && a.date === selectedDay!.format("YYYY-MM-DD"))))
                } else if (customAssignment) {
                    await putAssignment(foundShift!.scheduleId, foundShift!.shiftId, selectedDay!.format("YYYY-MM-DD"), selectedEmployees)
                    const newAssignment = {
                        scheduleId: foundShift!.scheduleId,
                        shiftDateId: foundShift!.shiftId + "#" + selectedDay!.format("YYYY-MM-DD"),
                        shiftId: foundShift!.shiftId,
                        date: selectedDay!.format("YYYY-MM-DD"),
                        assignees: selectedEmployees
                    }
                    setAssignments([...assignments, newAssignment])
                }
            } else {
                await putShift(
                    shift.scheduleId,
                    shift.shiftId,
                    editFormData.shiftName,
                    editFormData.daysOfWeek,
                    editFormData.startTime,
                    editFormData.endTime,
                    editFormData.position,
                    editFormData.staffNeeded.toString(),
                    selectedEmployees,
                    editFormData.isRecurring,
                    editFormData.shiftDate,
                    editFormData.notes
                );

                // Refresh shifts
                const updatedShifts = await getShifts(selectedSchedule.scheduleId);
                setShifts(updatedShifts);
            }

            onClose();
            toast.showSuccess('Shift updated successfully');
        } catch (error) {
            toast.showError('Error updating shift');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedEmployees([]);
        setStandardEmployees([]);
        setSearchQuery('');
        setAssignmentType('day');
        setTabValue(0);
        onClose();
    };

    /**
     * Compares a given shift with the current modal shift to check for time overlap
     * @param givenShift - The shift to compare against the modal shift
     * @returns 0 if no overlap, 1 if partial overlap, -1 if complete overlap
     */
    const compareShiftOverlap = (givenShift: Record<string, any>): number => {
        if (!shift?.startTime || !shift?.endTime) return 0;

        const modalStart = moment(shift.startTime, 'HH:mm');
        const modalEnd = moment(shift.endTime, 'HH:mm');
        const givenStart = moment(givenShift.startTime, 'HH:mm');
        const givenEnd = moment(givenShift.endTime, 'HH:mm');

        // Handle overnight shifts by adjusting end time if it's before start time
        if (modalEnd.isBefore(modalStart)) {
            modalEnd.add(1, 'day');
        }
        if (givenEnd.isBefore(givenStart)) {
            givenEnd.add(1, 'day');
        }

        // Check if there's no overlap
        if (givenEnd.isSameOrBefore(modalStart) || givenStart.isSameOrAfter(modalEnd)) {
            return 0; // No overlap
        }

        // Check for complete overlap (one shift fully contains the other or they're identical)
        const givenContainsModal = givenStart.isSameOrBefore(modalStart) && givenEnd.isSameOrAfter(modalEnd);

        if (givenContainsModal || (modalStart.isSame(givenStart) && modalEnd.isSame(givenEnd))) {
            return -1; // Complete overlap
        }

        // Otherwise, there's partial overlap
        return 1; // Partial overlap
    };

    const getAssignmentLevel = (employee: Employee) => {
        if (employee.availability) {
            let count = 0
            Object.keys(employee.availability).forEach((key) => {
                if (parseInt(key) >= parseInt(moment(shift.startTime, 'HH:mm').format('HH')) ||
                    parseInt(key) < parseInt(moment(shift.endTime, 'HH:mm').format('HH'))) {
                    if (employee.availability[key].includes("30")) {
                        count += 30
                    }
                    else {
                        count += 60
                    }
                }
            })
            return count;
        } else {
            return 0;
        }
    };

    const filteredAvailableEmployees = employees.filter(emp => {
        const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase());
        const hasCorrectPosition = emp.positionAssignments.filter(pos => pos.position === shift?.position).length > 0;
        const isNotAssigned = !selectedEmployees.includes(emp.id);
        const hasAvailability = moment(shift.endTime, 'HH:mm').diff(moment(shift.startTime, 'HH:mm'), 'hours') <= getAssignmentLevel(emp) / 60 &&
            (emp.employeeShifts.filter((s) => {
                return compareShiftOverlap(s) === 0
            }).length > 0 || emp.employeeShifts.length === 0);

        return hasCorrectPosition && matchesSearch && hasAvailability && (!hideAssigned || isNotAssigned);
    });

    const filteredSomeAvailableEmployees = employees.filter(emp => {
        const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase());
        const hasCorrectPosition = emp.positionAssignments.filter(pos => pos.position === shift?.position).length > 0;
        const isNotAssigned = !selectedEmployees.includes(emp.id);
        const hasPartialAvailability = getAssignmentLevel(emp) > 0 &&
            filteredAvailableEmployees.indexOf(emp) === -1 &&
            emp.employeeShifts.filter((s) => {
                return compareShiftOverlap(s) === 1
            }).length > 0;

        return hasCorrectPosition && matchesSearch && hasPartialAvailability && (!hideAssigned || isNotAssigned);
    });

    const filteredNonAvailableEmployees = employees.filter(emp => {
        const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase());
        const isNotAssigned = !selectedEmployees.includes(emp.id);
        const isUnavailable = (emp.positionAssignments.filter(pos => pos.position === shift?.position).length === 0
            || getAssignmentLevel(emp) === 0) || emp.employeeShifts.filter((s) => {
                return compareShiftOverlap(s) === -1
            }).length > 0;

        return matchesSearch && isUnavailable && (!hideAssigned || isNotAssigned);
    });

    const selectedCount = selectedEmployees.length;
    const spotsNeeded = editFormData.staffNeeded || 0;
    const isOverstaffed = selectedCount > spotsNeeded;

    const hasEditChanges = () => {
        if (!shift) return false;

        return (
            editFormData.shiftName !== shift.shiftName ||
            JSON.stringify(editFormData.daysOfWeek) !== JSON.stringify(shift.daysOfWeek) ||
            editFormData.startTime !== shift.startTime ||
            editFormData.endTime !== shift.endTime ||
            editFormData.position !== shift.position ||
            editFormData.staffNeeded !== shift.staffNeeded ||
            editFormData.isRecurring !== shift.isRecurring ||
            editFormData.shiftDate !== shift.shiftDate ||
            editFormData.notes !== shift.notes
        );
    };

    const hasAssignmentChanges = () => {
        return JSON.stringify(selectedEmployees.sort()) !== JSON.stringify((shift?.assignedStaff || []).sort());
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <ManageIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Manage Shift
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {shift?.shiftName} • {shift?.startTime} - {shift?.endTime}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab
                        label="Edit Shift"
                        icon={<EditIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', minHeight: 48 }}
                    />
                    <Tab
                        label="Assign Staff"
                        icon={<PersonAddIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', minHeight: 48 }}
                    />
                </Tabs>
            </Box>

            <DialogContent sx={{ px: 3 }}>
                {/* Edit Shift Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Staff Assignment Info */}
                        {selectedEmployees.length > 0 && (
                            <Alert severity="info" icon={<WarningIcon />}>
                                <Typography variant="body2" fontWeight={600} gutterBottom>
                                    {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} currently assigned
                                </Typography>
                                <Typography variant="caption">
                                    Changes to days or times will affect all assigned staff
                                </Typography>
                            </Alert>
                        )}

                        {/* Shift Name */}
                        <TextField
                            fullWidth
                            label="Shift Name"
                            name="shiftName"
                            value={editFormData.shiftName}
                            onChange={handleEditChange}
                            placeholder="e.g., Morning Shift, Evening Shift"
                            required
                            helperText={originalShiftName !== editFormData.shiftName ? "Changing shift name" : ""}
                        />

                        {/* Recurring Shift Checkbox */}
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isRecurring"
                                        checked={Boolean(editFormData.isRecurring)}
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label="Recurring Shift"
                            />
                            <FormHelperText>
                                {editFormData.isRecurring
                                    ? "This shift repeats weekly on selected days"
                                    : "This is a one-time shift"}
                            </FormHelperText>
                        </FormControl>

                        {/* Days of Week Selection (only for recurring) */}
                        {editFormData.isRecurring && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Select Days of Week *
                                </Typography>
                                <ToggleButtonGroup
                                    value={editFormData.daysOfWeek}
                                    onChange={handleDaysChange}
                                    fullWidth
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                        gap: 1,
                                        '& .MuiToggleButton-root': {
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            py: 1.5,
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    bgcolor: 'primary.dark'
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {daysOfWeek.map((day) => (
                                        <ToggleButton
                                            key={day.value}
                                            value={day.value}
                                            aria-label={day.fullLabel}
                                        >
                                            {day.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                {editFormData.daysOfWeek.length > 0 && (
                                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {editFormData.daysOfWeek.map((day: string) => {
                                            const dayInfo = daysOfWeek.find(d => d.value === day);
                                            return (
                                                <Chip
                                                    key={dayInfo ? dayInfo.value : String(day)}
                                                    label={dayInfo ? dayInfo.fullLabel : String(day)}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Shift Date (only for non-recurring) */}
                        {!editFormData.isRecurring && (
                            <TextField
                                fullWidth
                                label="Shift Date"
                                name="shiftDate"
                                type="date"
                                value={editFormData.shiftDate}
                                onChange={handleEditChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                helperText="This is a one-time shift"
                                required
                            />
                        )}

                        <Divider />

                        {/* Start and End Time */}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Start Time"
                                    name="startTime"
                                    type="time"
                                    value={editFormData.startTime}
                                    onChange={handleEditChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="End Time"
                                    name="endTime"
                                    type="time"
                                    value={editFormData.endTime}
                                    onChange={handleEditChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    required
                                />
                            </Grid>
                        </Grid>

                        {/* Shift Duration Display */}
                        {editFormData.startTime && editFormData.endTime && (
                            <Box
                                sx={{
                                    bgcolor: 'primary.50',
                                    p: 2,
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'primary.100'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <ClockIcon fontSize="small" color="primary" />
                                    <Typography variant="body2" fontWeight={600} color="primary">
                                        Shift Duration
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {(() => {
                                        const start = new Date(`2000-01-01T${editFormData.startTime}`);
                                        const end = new Date(`2000-01-01T${editFormData.endTime}`);
                                        let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                                        if (diff < 0) diff += 24;
                                        return `${diff.toFixed(1)} hours`;
                                    })()}
                                </Typography>
                            </Box>
                        )}

                        <Divider />

                        {/* Position/Role */}
                        <FormControl fullWidth>
                            <InputLabel>Position/Role</InputLabel>
                            <Select
                                name="position"
                                value={editFormData.position}
                                onChange={handleEditChange}
                                label="Position/Role"
                                required
                            >
                                {positions.map((position) => (
                                    <MenuItem key={position.position} value={position.position}>
                                        {position.positionName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Number of People Needed */}
                        <TextField
                            fullWidth
                            label="Number of People Needed"
                            name="staffNeeded"
                            type="number"
                            value={editFormData.staffNeeded}
                            onChange={handleEditChange}
                            InputProps={{
                                inputProps: { min: 1, max: 50 }
                            }}
                            helperText={
                                editFormData.staffNeeded < selectedEmployees.length
                                    ? `Warning: You have ${selectedEmployees.length} staff assigned`
                                    : `Currently ${selectedEmployees.length} of ${editFormData.staffNeeded} assigned`
                            }
                            required
                            error={editFormData.staffNeeded < selectedEmployees.length}
                        />

                        {/* Notes (Optional) */}
                        <TextField
                            fullWidth
                            label="Notes (Optional)"
                            name="notes"
                            value={editFormData.notes}
                            onChange={handleEditChange}
                            multiline
                            rows={3}
                            placeholder="Any additional information about this shift..."
                        />
                    </Box>
                </TabPanel>

                {/* Assign Staff Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        {/* Left Column - Employee List */}
                        <Box sx={{ flex: 1 }}>
                            {/* Search Bar and Filter */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Search employees..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={hideAssigned}
                                            onChange={(e) => setHideAssigned(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Hide Assigned"
                                    sx={{ whiteSpace: 'nowrap', pt: 0.5 }}
                                />
                            </Box>

                            {/* Employee List */}
                            <EmployeeSelectionList
                                availableEmployees={filteredAvailableEmployees}
                                partiallyAvailableEmployees={filteredSomeAvailableEmployees}
                                unavailableEmployees={filteredNonAvailableEmployees}
                                selectedEmployees={selectedEmployees}
                                assignmentType={assignmentType}
                                standardEmployees={standardEmployees}
                                onToggleEmployee={handleToggleEmployee}
                                selectedDay={selectedDay}
                            />
                        </Box>

                        {/* Right Column - Assignment Details */}
                        <Box sx={{ width: 280 }}>
                            {/* Assignment Type Toggle */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                                    Assignments
                                </Typography>
                                <ToggleButtonGroup
                                    value={assignmentType}
                                    exclusive
                                    onChange={(e, val) => val && setAssignmentType(val)}
                                    fullWidth
                                    orientation="vertical"
                                >
                                    <ToggleButton
                                        value="day"
                                        sx={{
                                            py: 1.5,
                                            textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            textTransform: 'none'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                            <EventIcon fontSize="small" sx={{ mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Current Assignments
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    For {selectedDay?.format('MMM D')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ToggleButton>
                                    <ToggleButton
                                        value="shift"
                                        sx={{
                                            py: 1.5,
                                            textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            textTransform: 'none'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                            <CalendarMonthIcon fontSize="small" sx={{ mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Standard Shift Assignments
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Regular staff for shift
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Shift Info */}
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    {assignmentType === 'day' ? 'This Assignment' : 'Shift Schedule'}
                                </Typography>
                                {assignmentType === 'day' ? (
                                    <Typography variant="body2" fontWeight={600}>
                                        {selectedDay?.format('dddd, MMMM D')}
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {editFormData.daysOfWeek?.map((day: string) => (
                                            <Chip
                                                key={day}
                                                label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', height: 22 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Staffing Status */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Staffing
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        color={selectedCount === spotsNeeded ? 'success.main' : isOverstaffed ? 'warning.main' : 'info.main'}
                                    >
                                        {selectedCount} / {spotsNeeded}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    height: 6,
                                    bgcolor: 'grey.200',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${Math.min((selectedCount / spotsNeeded) * 100, 100)}%`,
                                            bgcolor: selectedCount === spotsNeeded ? 'success.main' : isOverstaffed ? 'warning.main' : 'info.main',
                                            transition: 'all 0.3s'
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Selected Employees */}
                            {selectedEmployees.length > 0 ? (
                                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Selected ({selectedEmployees.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {selectedEmployees.map(empId => {
                                            const emp = employees.find(e => e.id === empId);
                                            return emp ? (
                                                <Box
                                                    key={empId}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 0.5
                                                    }}
                                                >
                                                    <Typography variant="caption" fontWeight={600}>
                                                        {emp.name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleEmployee(empId)}
                                                        sx={{ width: 20, height: 20 }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ) : null;
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    border: 2,
                                    borderStyle: 'dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        No employees selected
                                    </Typography>
                                </Box>
                            )}

                            {/* Info */}
                            <Alert severity="info" sx={{ mt: 2, py: 1 }}>
                                <Typography variant="caption">
                                    {assignmentType === 'day'
                                        ? `Current assignment changes are for ${selectedDay?.format('MMMM Do, YYYY')}`
                                        : 'Standard shift assignments will not affect any current assignments'
                                    }
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>
                </TabPanel>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={
                        loading ||
                        (tabValue === 0 &&
                            (!editFormData.shiftName ||
                                (editFormData.isRecurring && editFormData.daysOfWeek.length === 0) ||
                                !editFormData.startTime ||
                                !editFormData.endTime ||
                                !editFormData.position ||
                                (!hasEditChanges() && !hasAssignmentChanges()))
                        )
                    }
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}